
import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface BulkSetLimitsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedCount: number;
  onConfirm: (daily: number, monthly: number) => void;
  title: string;
  isLoading: boolean;
}

export const BulkSetLimitsDialog = ({
  open,
  onOpenChange,
  selectedCount,
  onConfirm,
  title,
  isLoading
}: BulkSetLimitsDialogProps) => {
  const [dailyLimit, setDailyLimit] = useState(10);
  const [monthlyLimit, setMonthlyLimit] = useState(100);

  const handleConfirm = () => {
    onConfirm(dailyLimit, monthlyLimit);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <p className="text-muted-foreground">
            This will update limits for {selectedCount} selected affiliates.
          </p>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="daily">Daily Limit</Label>
              <Input
                id="daily"
                type="number"
                min="0"
                value={dailyLimit}
                onChange={(e) => setDailyLimit(parseInt(e.target.value) || 0)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="monthly">Monthly Limit</Label>
              <Input
                id="monthly"
                type="number"
                min="0"
                value={monthlyLimit}
                onChange={(e) => setMonthlyLimit(parseInt(e.target.value) || 0)}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={isLoading}>
            {isLoading ? 'Updating...' : 'Update Limits'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface BulkResetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedCount: number;
  onConfirm: () => void;
  isLoading: boolean;
}

export const BulkResetDialog = ({
  open,
  onOpenChange,
  selectedCount,
  onConfirm,
  isLoading
}: BulkResetDialogProps) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reset to Default Limits</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <p className="text-muted-foreground">
            This will reset limits for {selectedCount} selected affiliates to their default values (Daily: 10, Monthly: 100).
          </p>
          <p className="text-sm text-destructive">
            This action cannot be undone.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={isLoading}>
            {isLoading ? 'Resetting...' : 'Reset to Defaults'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
